import { ExpandLess, ExpandMore, Save } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  Box,
  Theme
} from '@mui/material';
import { Loader } from '../../../components';
import { FC, ReactNode, useContext, useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { formatMoney } from '../../../helpers';
import { ISpec } from '../../../models';
import { finishSpec } from '../../../fetch';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import { UserContext } from '../../../context/user';
import makeStyles from '@mui/styles/makeStyles';

interface IBuildSummary {
  spec: ISpec;
  computedTotalPrice: number;
  upgradeCapOverage: number;
  contractSummary: string;
  tenDaySummary: string;
  upgradeCap: number;
  showSelectionDetails: (val: boolean) => void;
  lotPremium: number;
  basePrice: number;
  path?: string;
  shouldShowFinishSpecButton?: boolean;
}

interface ILocation {
  pathname: string;
  state: { showSelectionDetails: boolean };
}

export const BuildSummary: FC<IBuildSummary> = ({
  spec,
  computedTotalPrice,
  upgradeCapOverage,
  contractSummary,
  tenDaySummary,
  upgradeCap,
  showSelectionDetails,
  lotPremium,
  basePrice,
  path,
  shouldShowFinishSpecButton
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isFinishing, setIsFinishing] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get('redirect');

  const { user } = useContext(UserContext);
  const isAdmin = user?.userRoles?.includes('BL_Admin');

  const history = useHistory();
  const location: ILocation = useLocation();

  const classes = useStyles();

  useEffect(() => {
    if (location.state?.showSelectionDetails) {
      showSelectionDetails(true);
    }
  }, [location.state, showSelectionDetails]);

  const handleFinishSpec = async () => {
    const result = window.confirm('Are you sure you want to finish this spec?');

    if (result) {
      try {
        setIsFinishing(true);
        await finishSpec(spec.buildId);
        enqueueSnackbar('Spec finished!', {
          variant: 'success'
        });
        history.push(redirect ?? `${path ?? '/development'}/specs`);
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error finishing spec, please try again.`, {
          variant: 'error'
        });
      } finally {
        setIsFinishing(false);
      }
    }
  };

  return (
    <>
      {isFinishing && <Loader type="fullscreen" position="centered" />}
      <Grid container direction="column" spacing={1}>
        <Grid classes={{ root: classes.root }} item>
          <Typography color="primary" variant="h6" flexWrap="wrap">
            Build Summary
          </Typography>
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField title="Neighborhood" value={spec.neighborhoodDescription} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField title="Lot Number" value={spec.lotNumber} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField title="Plan" value={spec.planDescription} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField isMoney title="Base Price" value={basePrice} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField isMoney title="Lot Premium" value={lotPremium} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <BuildSummaryField title="Contract Option Summary" value={contractSummary} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          {/** TODO(Nick): Figure out how Ten Day Option Summary is calculated. */}
          <BuildSummaryField title="Ten Day Option Summary" value={tenDaySummary} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          {/** Sum of all above ^ money-based values: base price + lot premium + all selected upgrades */}
          <BuildSummaryField isMoney title="Total Price" value={computedTotalPrice} />
        </Grid>
        <Grid classes={{ root: classes.root }} item xs={12}>
          <Button
            variant="outlined"
            onClick={() => {
              showSelectionDetails(true);
            }}
            size="small"
            fullWidth
          >
            Selection Details
          </Button>
        </Grid>
        {spec?.isSpec && isAdmin && shouldShowFinishSpecButton && (
          <Grid item xs={12}>
            <Button
              color="primary"
              size="small"
              fullWidth
              variant="contained"
              disabled={isFinishing || !spec?.isTenDayComplete || !spec?.isContractComplete}
              onClick={handleFinishSpec}
              startIcon={<Save />}
            >
              Finish Spec
            </Button>
          </Grid>
        )}
        {/** UGPRADE CAP */}
        <Grid classes={{ root: classes.root }} item>
          <Typography color="primary" variant="h6">
            Option Deposit Cap
          </Typography>
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          {/**
           * If the Buyer does not purchase the house,SMB incurs out of pocket costs and
           * expenses based on the Buyer's Upgrade selections. To alleviate this risk,
           * if the Buyer’s upgrade selections exceed the Option Cap for their chosen
           * plan, the Buyer must pay a deposit equal to the sum of all upgrades minus
           * the "Option Cap" to SMB.
           */}
          <BuildSummaryField isMoney title="Option Deposit Cap" value={upgradeCap} />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          {/** sum of upgrades - cap */}
          <BuildSummaryField
            isMoney
            title="Exceeded Option Deposit Cap"
            value={formatMoney(upgradeCapOverage)}
            showColorEmphasis={upgradeCapOverage > 0}
          />
        </Grid>
        <Grid classes={{ root: classes.root }} item>
          <Reveal title="What is an option deposit cap?">
            <Typography paragraph>
              Purchasers have the opportunity to select certain options offered by Stone Martin
              Builders for their chosen house plan. SMB incurs out of pocket costs and expenses
              (specifically, material and labor costs) based on the purchaser’s selections. If the
              purchaser defaults under their contract and does not purchase the house, SMB incurs
              the monetary damages from the purchaser’s option selections.
            </Typography>
            <Typography paragraph>
              To alleviate this risk, if the purchaser’s option selections exceed the Option Deposit
              Cap for their chosen plan, the purchaser must pay a deposit equal to the sum of all
              options minus the "Option Cap Deposit" to Stone Martin Builders. If the purchaser has
              a contingent home to sale, then the purchaser must pay for all options selected over
              the homes base price.
            </Typography>
            <Typography>
              Refer to the Addendum F of your contract for additional details.
            </Typography>
          </Reveal>
        </Grid>
      </Grid>
    </>
  );
};

const Reveal: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion expanded={expanded} sx={{ boxShadow: 'none' }}>
      <AccordionSummary sx={{ padding: 0 }}>
        <Typography sx={{ alignSelf: 'center', flex: 1 }}>{title}</Typography>
        <IconButton onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess fontSize="large" /> : <ExpandMore fontSize="large" />}
        </IconButton>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
};

const BuildSummaryField: FC<{
  title: string;
  value: string | number;
  isMoney?: boolean;
  isEditing?: boolean;
  edit?(value: number): void;
  showColorEmphasis?: boolean;
}> = ({ title, value, isMoney, edit, isEditing, showColorEmphasis }) => {
  return (
    <Grid container direction="column">
      <Grid item>
        <Typography
          variant="subtitle2"
          sx={theme => ({
            fontWeight: 600,
            color: showColorEmphasis ? theme.palette.error.main : null
          })}
        >
          {title}
        </Typography>
      </Grid>
      <Grid item>
        {isEditing ? (
          <NumericFormat
            valueIsNumericString
            autoFocus
            thousandSeparator
            customInput={TextField}
            size="small"
            onValueChange={values => {
              edit(+values.value);
            }}
            prefix="$"
            value={value}
          />
        ) : (
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <Typography
                sx={theme => ({ color: showColorEmphasis ? theme.palette.error.main : null })}
                variant="body2"
              >
                {isMoney ? formatMoney(value) : value}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    '&.MuiGrid-item': {
      paddingTop: theme.spacing(0.5)
    }
  }
}));
